'use client';
/* eslint-disable max-len */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const UIContext = createContext();

export const UIProvider = ({ children }) => {
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [activeTab, setActiveTab] = useState('text');
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [isCapturing, setIsCapturing] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false); // Login popup state
  const pendingAfterLoginRef = useRef(null); // Callback to run after successful login (e.g. retry save book)
  const [headerSearchOpen, setHeaderSearchOpen] = useState(false); // Search bar под хедъра (само на homepage)
  // Mobile tap-to-place state — full image metadata for parity with drag-drop/direct upload.
  const [isPlacementMode, setIsPlacementMode] = useState(false);
  const [placementImageSource, setPlacementImageSource] = useState(null);
  // Grid overlay state
  const [showGrid, setShowGrid] = useState(false);
  // CategoryDropdown отворен – за viewport lock на мобилно (спиране на zoom)
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  // Layout drag from menu – highlight drop targets (photo pages only)
  const [isDraggingLayout, setIsDraggingLayout] = useState(false);
  // Ref set synchronously on drag start so ImageZone can block drop-target highlight before re-render
  const isDraggingLayoutRef = useRef(false);
  // Sidebar panels: LeftSidebar registers its close handler so canvas (e.g. PageFrame on layout drop) can close the sidebar
  const closeSidebarPanelsRef = useRef(null);
  // …и своя open handler, за да може popup/канвасът да отвори конкретен панел (напр. „подредба“)
  const openSidebarPanelRef = useRef(null);
  // Popup „Първо избери подредба“ – при опит за слагане на снимка върху страница
  // без подредба ('photo') и при клик върху вече избрана празна страница ('page').
  // Причината сменя обяснението в попъпа – двата случая изглеждат различно за човека.
  const [layoutHintOpen, setLayoutHintOpen] = useState(false);
  const [layoutHintReason, setLayoutHintReason] = useState('photo');

  // Canvas size management
  const updateCanvasSize = useCallback((newSize) => {
    setCanvasSize((prevSize) => {
      // Only update if dimensions actually changed
      if (prevSize.width !== newSize.width || prevSize.height !== newSize.height) {
        return {
          width: newSize.width,
          height: newSize.height,
          singlePageWidth: newSize.width / 2,
        };
      }
      return prevSize;
    });
  }, []);

  // Loading state management
  const setLoadingState = useCallback((isLoading, delay = 0) => {
    if (delay > 0) {
      setTimeout(() => setShowLoader(isLoading), delay);
    } else {
      setShowLoader(isLoading);
    }
  }, []);

  // Tab management
  const activateTab = useCallback((tabName) => {
    if (['text', 'shape', 'emoji', 'background', 'layout'].includes(tabName)) {
      setActiveTab(tabName);
    }
  }, []);

  // Menu management
  const toggleShapeMenu = useCallback(() => {
    setShowShapeMenu((prev) => !prev);
  }, []);

  const closeAllMenus = useCallback(() => {
    setShowShapeMenu(false);
  }, []);

  const closeSidebarPanels = useCallback(() => {
    closeSidebarPanelsRef.current?.();
  }, []);

  const registerCloseSidebarPanels = useCallback((fn) => {
    closeSidebarPanelsRef.current = fn;
  }, []);

  const openSidebarPanel = useCallback((panelId) => {
    openSidebarPanelRef.current?.(panelId);
  }, []);

  const registerOpenSidebarPanel = useCallback((fn) => {
    openSidebarPanelRef.current = fn;
  }, []);

  // „Първо избери подредба“ popup
  const openLayoutHint = useCallback((reason) => {
    setLayoutHintReason(reason === 'page' ? 'page' : 'photo');
    setLayoutHintOpen(true);
  }, []);
  const closeLayoutHint = useCallback(() => setLayoutHintOpen(false), []);

  // Tap-to-place helpers
  // imageData: { src: string, thumbnailUrl?, editorUrl?, originalUrl?, naturalWidth?, naturalHeight?, faces? }
  const startPlacement = useCallback(
    (imageData) => {
      const src = typeof imageData === 'string' ? imageData : imageData?.src;
      if (!src) {
        return;
      }
      // Toggle off if same image tapped again
      if (isPlacementMode && placementImageSource?.src === src) {
        setIsPlacementMode(false);
        setPlacementImageSource(null);
      } else {
        setPlacementImageSource(typeof imageData === 'string' ? { src: imageData } : imageData);
        setIsPlacementMode(true);
      }
    },
    [isPlacementMode, placementImageSource],
  );

  const cancelPlacement = useCallback(() => {
    setIsPlacementMode(false);
    setPlacementImageSource(null);
  }, []);

  // Grid toggle
  const toggleGrid = useCallback(() => {
    setShowGrid((prev) => !prev);
  }, []);

  // Pending action after login (e.g. save book when opened from canvas without session)
  const setPendingAfterLogin = useCallback((fn) => {
    pendingAfterLoginRef.current = fn;
  }, []);

  const runPendingAfterLogin = useCallback(() => {
    const fn = pendingAfterLoginRef.current;
    pendingAfterLoginRef.current = null;
    fn?.();
  }, []);

  // Мемоизиране на value за по-добра производителност
  const value = useMemo(
    () => ({
      // UI State
      showShapeMenu,
      setShowShapeMenu,
      showLoader,
      setShowLoader,
      activeTab,
      setActiveTab,
      canvasSize,
      setCanvasSize,
      isCapturing,
      setIsCapturing,
      loginOpen,
      setLoginOpen,
      setPendingAfterLogin,
      runPendingAfterLogin,
      headerSearchOpen,
      setHeaderSearchOpen,
      // placement mode
      isPlacementMode,
      placementImageSrc: placementImageSource?.src ?? null,
      placementData: placementImageSource, // full { src, thumbnailUrl }
      startPlacement,
      cancelPlacement,
      // Grid overlay
      showGrid,
      setShowGrid,
      toggleGrid,
      categoryDropdownOpen,
      setCategoryDropdownOpen,
      // Layout drag (for highlighting drop targets)
      isDraggingLayout,
      setIsDraggingLayout,
      isDraggingLayoutRef,
      // „Първо избери подредба“ popup
      layoutHintOpen,
      layoutHintReason,
      openLayoutHint,
      closeLayoutHint,

      // Utility functions
      updateCanvasSize,
      setLoadingState,
      activateTab,
      toggleShapeMenu,
      closeAllMenus,
      closeSidebarPanels,
      registerCloseSidebarPanels,
      openSidebarPanel,
      registerOpenSidebarPanel,
    }),
    [
      showShapeMenu,
      showLoader,
      activeTab,
      canvasSize,
      isCapturing,
      loginOpen,
      headerSearchOpen,
      isPlacementMode,
      placementImageSource,
      showGrid,
      categoryDropdownOpen,
      isDraggingLayout,
      layoutHintOpen,
      layoutHintReason,
      openLayoutHint,
      closeLayoutHint,
      startPlacement,
      cancelPlacement,
      updateCanvasSize,
      setLoadingState,
      activateTab,
      toggleShapeMenu,
      closeAllMenus,
      closeSidebarPanels,
      registerCloseSidebarPanels,
      openSidebarPanel,
      registerOpenSidebarPanel,
      toggleGrid,
      setPendingAfterLogin,
      runPendingAfterLogin,
    ],
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};
